import { Link, useParams } from "react-router-dom";
import type { PublicProfile } from "../../shared/api";
import { metaFor } from "../../shared/seo";
import { api } from "../lib/api";
import { useMeta } from "../lib/seo";
import { useRouteData, userKey } from "../lib/routeData";
import { origin } from "../lib/origin";
import { Spinner, ErrorState, Empty } from "../components/States";
import { VerificationBadge } from "../components/Badge";
import { Stars } from "../components/Stars";
import { TimeAgo } from "../lib/format";

export function Reviewer() {
  const { username } = useParams();
  const { data, error, loading } = useRouteData<PublicProfile>(userKey(username ?? ""), () =>
    api.user(username!),
  );

  useMeta(
    data
      ? metaFor({
          route: "user",
          base: origin(),
          username: data.username,
          reviewCount: data.reviews.total,
        })
      : null,
  );

  if (error) return <ErrorState message={error} />;
  if (loading || !data) return <Spinner />;

  return (
    <div className="narrow-lg">
      <h1>@{data.username}</h1>
      <div className="score-band u-49f14f8">
        <div className="score-cell">
          <div className="label">Reputation</div>
          <div className="big rating-num">{Math.round(data.reputation)}</div>
        </div>
        <div className="score-cell">
          <div className="label">Reviews</div>
          <div className="big rating-num">{data.reviews.total}</div>
        </div>
        <div className="score-cell">
          <div className="label">Verified</div>
          <div className="big rating-num">{data.verifiedCount}</div>
        </div>
        <div className="score-cell">
          <div className="label">Member since</div>
          <div className="big rating-num"><TimeAgo iso={data.memberSince} /></div>
        </div>
      </div>
      <p className="muted">
        Reputation is earned from published reviews, proof of usage, and other readers finding a
        review helpful. <Link to="/methodology">See exactly how it moves →</Link>
      </p>

      <h2 className="section">Reviews</h2>
      {data.reviews.items.length === 0 ? (
        <Empty title="No published reviews yet" hint="Reviews appear here once they clear our integrity checks." />
      ) : (
        <div className="grid gap-12">
          {data.reviews.items.map((r) => (
            <article key={r.id} className="card p-16">
              <div className="row between items-baseline">
                <Link to={`/${r.entity.type}/${r.entity.slug}`}><strong>{r.entity.name}</strong></Link>
                <VerificationBadge level={r.verificationLevel} />
              </div>
              <div className="row u-d6a1bcb">
                <Stars value={r.ratingOverall} />
                <span className="muted u-474a305"><TimeAgo iso={r.createdAt} /></span>
              </div>
              {r.title ? <h3 className="u-557d84c">{r.title}</h3> : null}
              {r.body ? <p>{r.body}</p> : null}
              {r.helpfulCount > 0 ? (
                <p className="muted u-d8c9dc5">
                  {r.helpfulCount} found this helpful
                </p>
              ) : null}
            </article>
          ))}
        </div>
      )}
    </div>
  );
}
